import React, { useMemo, useState } from "react";
import {
  Paper, Box, TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Checkbox, IconButton, Tooltip, Chip, TablePagination, Skeleton,
  Typography, Button, Switch, Stack
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

const ColorDot = ({ color }) => (
  <Box sx={{ width: 16, height: 16, borderRadius: "50%", bgcolor: color || "#999", border: "1px solid #0001" }} />
);

const PipelineStageTable = ({
  stages = [],
  rowCount = 0,
  page = 0,
  pageSize = 50,
  onPageChange,
  onPageSizeChange,
  onSearch,
  isLoading = false,
  onEditStage,
  onDeleteStage,
  onToggleStatus,

  // bulk
  onBulkActivate,
  onBulkDeactivate,
  onBulkDelete,
}) => {
  const [localSearch, setLocalSearch] = useState("");
  const [selected, setSelected] = useState(new Set());

  const ordered = useMemo(
    () => stages.slice().sort((a, b) => (a.ordem || 0) - (b.ordem || 0)),
    [stages]
  );

  const selectedArr = Array.from(selected);
  const allOnPageSelected = ordered.length > 0 && ordered.every((s) => selected.has(s.id));

  const isRowSelected = (id) => selected.has(id);
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAll = () =>
    setSelected((prev) => {
      const ids = ordered.map((s) => s.id);
      const next = new Set(prev);
      if (allOnPageSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });

  return (
    <Paper variant="outlined">
      {/* Busca */}
      <Box sx={{ p: 1.5 }}>
        <TextField
          fullWidth
          placeholder="Buscar"
          value={localSearch}
          onChange={(e) => setLocalSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch?.(localSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Ações em massa */}
      {selectedArr.length > 0 && (
        <Box sx={{ mx: 1.5, mb: 1, p: 1, borderRadius: 1, bgcolor: "action.hover",
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography variant="body2">{selectedArr.length} selecionado(s)</Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button size="small" onClick={() => onBulkActivate?.(selectedArr)}>Ativar</Button>
            <Button size="small" onClick={() => onBulkDeactivate?.(selectedArr)}>Desativar</Button>
            <Button size="small" color="error" onClick={() => onBulkDelete?.(selectedArr)}>Excluir</Button>
          </Box>
        </Box>
      )}

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={48}>
                <Checkbox
                  checked={allOnPageSelected}
                  indeterminate={!allOnPageSelected && selectedArr.length > 0}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell>Ordem</TableCell>
              <TableCell>Etapa</TableCell>
              <TableCell>Probabilidade</TableCell>
              <TableCell>Cor</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>SLA (dias)</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Ações</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3, 4, 5, 6].map((row) => (
                  <TableRow key={row}>
                    <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={32} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width="70%" height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={48} height={20} /></TableCell>
                    <TableCell><Skeleton variant="rectangular" width={24} height={24} /></TableCell>
                    <TableCell><Skeleton variant="text" width={56} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={48} height={20} /></TableCell>
                    <TableCell><Skeleton variant="text" width={48} height={20} /></TableCell>
                    <TableCell align="right"><Skeleton variant="rounded" width={72} height={28} sx={{ ml: "auto" }} /></TableCell>
                  </TableRow>
                ))}
              </>
            ) : ordered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      Nenhuma etapa encontrada.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              ordered.map((s) => {
                const checked = isRowSelected(s.id);
                return (
                  <TableRow key={s.id} hover selected={checked}>
                    <TableCell>
                      <Checkbox checked={checked} onChange={() => toggleSelect(s.id)} />
                    </TableCell>
                    <TableCell>{s.ordem ?? ""}</TableCell>
                    <TableCell>{s.nome}</TableCell>
                    <TableCell>
                      <Chip size="small" label={s.probabilidade != null ? `${s.probabilidade}%` : "—"} />
                    </TableCell>
                    <TableCell><ColorDot color={s.cor} /></TableCell>
                    <TableCell>
                      {s.tipo === "ganho" ? "Ganho" : s.tipo === "perdido" ? "Perdido" : "Normal"}
                    </TableCell>
                    <TableCell>{s.sla_dias ?? "—"}</TableCell>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Switch
                          size="small"
                          checked={!!s.status}
                          onChange={() => onToggleStatus?.(s)}
                        />
                        <Chip
                          size="small"
                          label={s.status ? "Ativa" : "Inativa"}
                          color={s.status ? "success" : "default"}
                          variant="outlined"
                        />
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Editar">
                        <IconButton size="small" onClick={() => onEditStage?.(s)}>
                          <EditRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton size="small" onClick={() => onDeleteStage?.(s.id)}>
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginação */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: 1 }}>
        <TablePagination
          component="div"
          count={rowCount}
          page={page}
          rowsPerPage={pageSize}
          onPageChange={(_, newPage) => onPageChange?.(newPage)}
          onRowsPerPageChange={(e) => onPageSizeChange?.(parseInt(e.target.value, 10))}
          labelRowsPerPage="Linhas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} of ${count}`}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
    </Paper>
  );
};

export default PipelineStageTable;
